import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Delete, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Mascot } from "@/components/Mascot";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "enter"];

const Key = ({ k, onPress, disabled }) => {
  const isNum = /\d/.test(k);
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      disabled={disabled}
      onClick={() => onPress(k)}
      data-testid={`pin-key-${k}`}
      className={`h-16 rounded-3xl text-2xl font-heading font-medium flex items-center justify-center transition-colors ${
        k === "enter" ? "bg-pink-500 text-white hover:bg-pink-600" : isNum ? "bg-white text-gray-700 hover:bg-pink-50 border border-pink-100" : "text-pink-400 hover:bg-pink-100/60"
      }`}
    >
      {k === "del" ? <Delete className="w-6 h-6" /> : k === "enter" ? (disabled ? <Loader2 className="w-6 h-6 animate-spin" /> : <ArrowRight className="w-6 h-6" />) : k}
    </motion.button>
  );
};

export default function PinLogin() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(0);

  const submit = useCallback(async (value) => {
    if (value.length < 4) {
      setError("PIN minimal 4 digit");
      return;
    }
    setBusy(true);
    try {
      await login(value);
    } catch (e) {
      setError(errMsg(e));
      setPin("");
      setShake((s) => s + 1);
    } finally {
      setBusy(false);
    }
  }, [login]);

  const press = useCallback((k) => {
    setError("");
    if (k === "del") setPin((p) => p.slice(0, -1));
    else if (k === "enter") submit(pin);
    else setPin((p) => (p.length < 6 ? p + k : p));
  }, [pin, submit]);

  useEffect(() => {
    const h = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
      else if (e.key === "Enter") press("enter");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [press]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-100 via-pink-50 to-white bg-dots flex flex-col items-center justify-center px-6 py-10">
      <Mascot pose="wave" className="w-36 sm:w-44" />
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-pink-500 mt-2">windiku<span className="text-sky-400">.com</span></h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">Halo! Masukkan PIN kamu dulu ya</p>
      <div key={shake} className={`flex gap-3 mb-3 h-5 ${shake ? "shake" : ""}`} data-testid="pin-input-field">
        {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
          <span key={i} className={`w-4 h-4 rounded-full border-2 transition-colors ${i < pin.length ? "bg-pink-500 border-pink-500" : "border-pink-300"}`} />
        ))}
      </div>
      <p data-testid="pin-error" className="text-sm text-rose-500 h-5 mb-4">{error}</p>
      <div className="grid grid-cols-3 gap-3 w-full max-w-[300px]">
        {KEYS.map((k) => <Key key={k} k={k} onPress={press} disabled={busy && k === "enter"} />)}
      </div>
      <p className="text-xs text-gray-400 mt-8 text-center max-w-xs">Lupa PIN? Minta admin untuk menggantinya lewat Pengaturan dengan kode verifikasi email admin.</p>
    </div>
  );
}
